import axios from "axios";

// Use current browser origin for API calls so custom domains work.
// Falls back to REACT_APP_BACKEND_URL for dev/preview environments.
const BACKEND_URL = typeof window !== "undefined" && window.location.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
});

// Intercepteur global : redirection vers login sur 401
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            if (!window.location.pathname.includes("/login")) {
                window.location.href = "/login";
            }
        }
        return Promise.reject(error);
    }
);

// Users
export const getUsers = (params) => api.get("/users", { params }).then((r) => r.data);
export const getUser = (id) => api.get(`/users/${id}`).then((r) => r.data);
export const createUser = (data) => api.post("/users", data).then((r) => r.data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data).then((r) => r.data);

// Departments
export const getDepartments = () => api.get("/departments").then((r) => r.data);
export const getDepartmentMembers = (deptId) => api.get(`/departments/${deptId}/members`).then((r) => r.data);
export const createDepartment = (data) => api.post("/departments", data).then((r) => r.data);
export const updateDepartment = (id, data) => api.put(`/departments/${id}`, data).then((r) => r.data);
export const deleteDepartment = (id) => api.delete(`/departments/${id}`).then((r) => r.data);
export const removeDepartmentMember = (deptId, data) => api.post(`/departments/${deptId}/remove-member`, data).then((r) => r.data);
export const bulkRemoveDepartmentMembers = (deptId, data) => api.post(`/departments/${deptId}/bulk-remove-members`, data).then((r) => r.data);
export const getDepartmentExclusions = (deptId) => api.get(`/departments/${deptId}/exclusions`).then((r) => r.data);
export const deleteDepartmentExclusion = (exclusionId) => api.delete(`/department-exclusions/${exclusionId}`).then((r) => r.data);

// Positions
export const getPositions = () => api.get("/positions").then((r) => r.data);
export const createPosition = (data) => api.post("/positions", data).then((r) => r.data);
export const updatePosition = (id, data) => api.put(`/positions/${id}`, data).then((r) => r.data);
export const deletePosition = (id) => api.delete(`/positions/${id}`).then((r) => r.data);

// Bulk operations
export const bulkAssignPosition = (data) => api.post("/users/bulk-assign-position", data).then((r) => r.data);
export const bulkUpdateUsers = (data) => api.post("/users/bulk-update", data).then((r) => r.data);

// Tickets
export const getTickets = (params) => api.get("/tickets", { params }).then((r) => r.data);
export const searchTicketsGlobal = (params) => api.get("/tickets/search/global", { params }).then((r) => r.data);
export const getTicket = (id, viewerId) => api.get(`/tickets/${id}`, { params: viewerId ? { viewer_id: viewerId } : {} }).then((r) => r.data);
export const createTicket = (data) => api.post("/tickets", data).then((r) => r.data);
export const updateTicket = (id, data, actorId) =>
    api.put(`/tickets/${id}`, data, { params: { actor_id: actorId } }).then((r) => r.data);
export const assignTicket = (ticketId, assigneeId, actorId) =>
    api.post(`/tickets/${ticketId}/assign`, null, { params: { assignee_id: assigneeId, actor_id: actorId } }).then((r) => r.data);

// Comments
export const getComments = (ticketId, includeInternal = false) =>
    api.get(`/tickets/${ticketId}/comments`, { params: { include_internal: includeInternal } }).then((r) => r.data);
export const createComment = (ticketId, data, authorId) =>
    api.post(`/tickets/${ticketId}/comments`, data, { params: { author_id: authorId } }).then((r) => r.data);

// Approvals
export const getPendingApprovals = (approverId) =>
    api.get("/approvals/pending", { params: { approver_id: approverId } }).then((r) => r.data);
export const decideApproval = (approvalId, status, comment, actorId) =>
    api.post(`/approvals/${approvalId}/decide`, null, { params: { status, comment, actor_id: actorId } }).then((r) => r.data);

// User Assets
export const getUserAssets = (userId, params) => api.get(`/users/${userId}/assets`, { params }).then((r) => r.data);
export const getUserAssetsSummary = (userId) => api.get(`/users/${userId}/assets/summary`).then((r) => r.data);
export const createUserAsset = (userId, data) => api.post(`/users/${userId}/assets`, data).then((r) => r.data);
export const updateAsset = (assetId, data) => api.put(`/assets/${assetId}`, data).then((r) => r.data);
export const returnAsset = (assetId, data) => api.post(`/assets/${assetId}/return`, data).then((r) => r.data);
export const transferAsset = (assetId, data) => api.post(`/assets/${assetId}/transfer`, data).then((r) => r.data);
export const markAssetLost = (assetId, data) => api.post(`/assets/${assetId}/mark-lost`, data).then((r) => r.data);
export const markAssetBroken = (assetId, data) => api.post(`/assets/${assetId}/mark-broken`, data).then((r) => r.data);
export const getAssetHistory = (assetId) => api.get(`/assets/${assetId}/history`).then((r) => r.data);

// Devices
export const getUserDevices = (userId, params) => api.get(`/users/${userId}/devices`, { params }).then((r) => r.data);
export const createUserDevice = (userId, data) => api.post(`/users/${userId}/devices`, data).then((r) => r.data);
export const updateDevice = (deviceId, data) => api.put(`/devices/${deviceId}`, data).then((r) => r.data);
export const returnDevice = (deviceId, data) => api.post(`/devices/${deviceId}/return`, data).then((r) => r.data);
export const deleteDevice = (deviceId) => api.delete(`/devices/${deviceId}`).then((r) => r.data);

// Licenses
export const getUserLicenses = (userId, params) => api.get(`/users/${userId}/licenses`, { params }).then((r) => r.data);
export const createUserLicense = (userId, data) => api.post(`/users/${userId}/licenses`, data).then((r) => r.data);
export const updateLicense = (licenseId, data) => api.put(`/licenses/${licenseId}`, data).then((r) => r.data);
export const revokeLicense = (licenseId, data) => api.post(`/licenses/${licenseId}/revoke`, data).then((r) => r.data);
export const deleteLicense = (licenseId) => api.delete(`/licenses/${licenseId}`).then((r) => r.data);

// Custom fields
export const getCustomFields = () => api.get("/custom-fields").then((r) => r.data);
export const getAllCustomFields = () => api.get("/custom-fields/all").then((r) => r.data);
export const createCustomField = (data) => api.post("/custom-fields", data).then((r) => r.data);
export const updateCustomField = (id, data) => api.put(`/custom-fields/${id}`, data).then((r) => r.data);
export const deleteCustomField = (id) => api.delete(`/custom-fields/${id}`).then((r) => r.data);

// Export
export const exportUsersExcel = (params = {}) => api.get("/export/users/excel", { params, responseType: "blob" });

// Inventory
export const getInventoryItems = (params) => api.get("/inventory/items", { params }).then((r) => r.data);
export const getInventoryItem = (id) => api.get(`/inventory/items/${id}`).then((r) => r.data);
export const createInventoryItem = (data) => api.post("/inventory/items", data).then((r) => r.data);
export const updateInventoryItem = (id, data) => api.put(`/inventory/items/${id}`, data).then((r) => r.data);
export const deleteInventoryItem = (id) => api.delete(`/inventory/items/${id}`).then((r) => r.data);
export const assignInventoryItem = (id, data) => api.post(`/inventory/items/${id}/assign`, data).then((r) => r.data);
export const unassignInventoryItem = (id, data) => api.post(`/inventory/items/${id}/unassign`, data).then((r) => r.data);
export const getItemHolders = (id) => api.get(`/inventory/items/${id}/holders`).then((r) => r.data);
export const transferInventoryItem = (id, data) => api.post(`/inventory/items/${id}/transfer`, data).then((r) => r.data);
export const maintenanceInventoryItem = (id, data) => api.post(`/inventory/items/${id}/maintenance`, data).then((r) => r.data);
export const retireInventoryItem = (id, data) => api.post(`/inventory/items/${id}/retire`, data).then((r) => r.data);
export const markLostInventoryItem = (id, data) => api.post(`/inventory/items/${id}/mark-lost`, data).then((r) => r.data);
export const markBrokenInventoryItem = (id, data) => api.post(`/inventory/items/${id}/mark-broken`, data).then((r) => r.data);
export const restoreInventoryItem = (id, data) => api.post(`/inventory/items/${id}/restore`, data).then((r) => r.data);
export const getInventoryStats = () => api.get("/inventory/stats").then((r) => r.data);
export const confirmInventoryRebill = (id) => api.post(`/inventory/items/${id}/rebill/confirm`).then((r) => r.data);
export const reopenInventoryRebill = (id) => api.post(`/inventory/items/${id}/rebill/reopen`).then((r) => r.data);
export const getInventoryCategories = () => api.get("/inventory/categories").then((r) => r.data);
export const getInventoryByUser = (userId) => api.get(`/inventory/by-user/${userId}`).then((r) => r.data);
export const exportInventoryExcel = (params = {}) => api.get("/inventory/export/excel", { params, responseType: "blob" });
export const getInventoryEmployees = (params) => api.get("/inventory/employees", { params }).then((r) => r.data);
export const createInventoryEmployee = (data) => api.post("/inventory/employees", data).then((r) => r.data);
export const updateInventoryEmployee = (id, data) => api.put(`/inventory/employees/${id}`, data).then((r) => r.data);
export const deleteInventoryEmployee = (id) => api.delete(`/inventory/employees/${id}`).then((r) => r.data);
export const getEmployeeSheet = (id) => api.get(`/inventory/employees/${id}/sheet`).then((r) => r.data);
export const replaceInventoryItem = (id, data) => api.post(`/inventory/items/${id}/replace`, data).then((r) => r.data);
export const getNextAssetTag = (category) => api.get("/inventory/next-tag", { params: { category } }).then((r) => r.data);
export const getInventoryItemByTag = (tag) => api.get(`/inventory/by-tag/${encodeURIComponent(tag)}`).then((r) => r.data);
export const regenerateAssetTag = (id, data = {}) => api.post(`/inventory/items/${id}/regenerate-tag`, data).then((r) => r.data);
export const uploadInventoryAttachment = (itemId, file, displayName, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("display_name", displayName || "");
    if (opts.category) fd.append("category", opts.category);
    if (opts.historyId) fd.append("history_id", opts.historyId);
    if (opts.docType) fd.append("doc_type", opts.docType);
    if (opts.signedDate) fd.append("signed_date", opts.signedDate);
    return api.post(`/inventory/items/${itemId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const renameInventoryAttachment = (id, displayName) => api.put(`/inventory/attachments/${id}`, { display_name: displayName }).then((r) => r.data);
export const deleteInventoryAttachment = (id) => api.delete(`/inventory/attachments/${id}`).then((r) => r.data);
export const inventoryAttachmentUrl = (id) => `${API_BASE}/inventory/attachments/${id}/download`;
export const addInventoryLink = (itemId, data) => api.post(`/inventory/items/${itemId}/links`, data).then((r) => r.data);
export const linkEmployeeSignedDoc = (empId, data) => api.post(`/inventory/employees/${empId}/handover-doc-link`, data).then((r) => r.data);

// Permissions (plan de rôles & accès)
export const getPermissionCatalog = () => api.get("/permissions/catalog").then((r) => r.data);
export const updateRolePermissions = (role, permissions) => api.put(`/permissions/roles/${role}`, { permissions }).then((r) => r.data);
export const resetRolePermissions = (role) => api.delete(`/permissions/roles/${role}`).then((r) => r.data);
export const getUsersWithAccess = () => api.get("/permissions/users").then((r) => r.data);
export const setUserPermissions = (userId, permissions) => api.put(`/permissions/users/${userId}`, { permissions }).then((r) => r.data);

// Printers & consumables
export const getPrinterSites = () => api.get("/printers/sites").then((r) => r.data);
export const createPrinterSite = (data) => api.post("/printers/sites", data).then((r) => r.data);
export const updatePrinterSite = (id, data) => api.put(`/printers/sites/${id}`, data).then((r) => r.data);
export const deletePrinterSite = (id) => api.delete(`/printers/sites/${id}`).then((r) => r.data);
export const getPrinterCompanies = (params) => api.get("/printers/companies", { params }).then((r) => r.data);
export const createPrinterCompany = (data) => api.post("/printers/companies", data).then((r) => r.data);
export const updatePrinterCompany = (id, data) => api.put(`/printers/companies/${id}`, data).then((r) => r.data);
export const deletePrinterCompany = (id) => api.delete(`/printers/companies/${id}`).then((r) => r.data);
export const getPrinters = (params) => api.get("/printers", { params }).then((r) => r.data);
export const getPrinter = (id) => api.get(`/printers/${id}`).then((r) => r.data);
export const createPrinter = (data) => api.post("/printers", data).then((r) => r.data);
export const updatePrinter = (id, data) => api.put(`/printers/${id}`, data).then((r) => r.data);
export const deletePrinter = (id) => api.delete(`/printers/${id}`).then((r) => r.data);
export const addPrinterCheck = (id, data) => api.post(`/printers/${id}/checks`, data).then((r) => r.data);
export const deletePrinterCheck = (checkId) => api.delete(`/printers/checks/${checkId}`).then((r) => r.data);
export const getConsumables = (params) => api.get("/printers/consumables", { params }).then((r) => r.data);
export const createConsumable = (data) => api.post("/printers/consumables", data).then((r) => r.data);
export const updateConsumable = (id, data) => api.put(`/printers/consumables/${id}`, data).then((r) => r.data);
export const deleteConsumable = (id) => api.delete(`/printers/consumables/${id}`).then((r) => r.data);
export const addConsumableMovement = (id, data) => api.post(`/printers/consumables/${id}/movements`, data).then((r) => r.data);
export const getConsumableMovements = (id) => api.get(`/printers/consumables/${id}/movements`).then((r) => r.data);
export const getPrinterMovements = (params) => api.get("/printers/movements", { params }).then((r) => r.data);
export const deletePrinterMovement = (id) => api.delete(`/printers/movements/${id}`).then((r) => r.data);
export const getPrinterStats = () => api.get("/printers/stats").then((r) => r.data);
export const exportPrintersExcel = () => api.get("/printers/export/excel", { responseType: "blob" });
export const addPrinterDocument = (printerId, data) => api.post(`/printers/${printerId}/documents`, data).then((r) => r.data);
export const addPrinterCompanyDocument = (companyId, data) => api.post(`/printers/companies/${companyId}/documents`, data).then((r) => r.data);
export const updatePrinterDocument = (id, data) => api.put(`/printers/documents/${id}`, data).then((r) => r.data);
export const deletePrinterDocument = (id) => api.delete(`/printers/documents/${id}`).then((r) => r.data);
export const inventoryHandoverDocUrl = (itemId, kind) => `${API_BASE}/inventory/items/${itemId}/handover-doc?kind=${kind}`;
export const employeeHandoverDocUrl = (empId, kind) => `${API_BASE}/inventory/employees/${empId}/handover-doc?kind=${kind}`;
export const uploadEmployeeSignedDoc = (empId, file, docType, signedDate) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    if (signedDate) fd.append("signed_date", signedDate);
    return api.post(`/inventory/employees/${empId}/handover-doc-scan`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const getInventoryBadgeLabelPng = (id) =>
    api.get(`/inventory/items/${id}/label-badge.png`, { params: { origin: window.location.origin }, responseType: "blob" }).then((r) => r.data);
export const downloadInventoryLabelLbx = (id, tape, withBarcode, fields = {}) =>
    api.get(`/inventory/items/${id}/label.lbx`, {
        responseType: "blob",
        params: {
            tape,
            barcode: withBarcode ? 1 : 0,
            model: fields.model ? 1 : 0,
            serial: fields.serial ? 1 : 0,
            qr_only: fields.qrOnly ? 1 : 0,
            layout: fields.layout || "standard",
            origin: window.location.origin,
        },
    }).then((r) => r.data);

// Catalogs
export const getSoftwareCatalog = () => api.get("/catalog/software").then((r) => r.data);
export const createSoftware = (data) => api.post("/catalog/software", data).then((r) => r.data);
export const updateSoftware = (id, data) => api.put(`/catalog/software/${id}`, data).then((r) => r.data);
export const deleteSoftware = (id) => api.delete(`/catalog/software/${id}`).then((r) => r.data);
export const getSoftwareCategories = () => api.get("/catalog/software/categories").then((r) => r.data);
export const createSoftwareCategory = (data) => api.post("/catalog/software/categories", data).then((r) => r.data);
export const updateSoftwareCategory = (id, data) => api.put(`/catalog/software/categories/${id}`, data).then((r) => r.data);
export const deleteSoftwareCategory = (id) => api.delete(`/catalog/software/categories/${id}`).then((r) => r.data);
export const getSoftwareRequests = () => api.get("/catalog/software/requests").then((r) => r.data);
export const promoteSoftwareRequest = (data) => api.post("/catalog/software/promote", data).then((r) => r.data);

export const getResourcesCatalog = () => api.get("/catalog/resources").then((r) => r.data);
export const createResource = (data) => api.post("/catalog/resources", data).then((r) => r.data);
export const updateResource = (id, data) => api.put(`/catalog/resources/${id}`, data).then((r) => r.data);
export const deleteResource = (id) => api.delete(`/catalog/resources/${id}`).then((r) => r.data);

export const getEquipmentCatalog = () => api.get("/catalog/equipment").then((r) => r.data);
export const createEquipment = (data) => api.post("/catalog/equipment", data).then((r) => r.data);
export const updateEquipment = (id, data) => api.put(`/catalog/equipment/${id}`, data).then((r) => r.data);
export const deleteEquipment = (id) => api.delete(`/catalog/equipment/${id}`).then((r) => r.data);

export const getWorkstationTemplates = () => api.get("/catalog/workstation-templates").then((r) => r.data);
export const createWorkstationTemplate = (data) => api.post("/catalog/workstation-templates", data).then((r) => r.data);
export const updateWorkstationTemplate = (id, data) => api.put(`/catalog/workstation-templates/${id}`, data).then((r) => r.data);
export const deleteWorkstationTemplate = (id) => api.delete(`/catalog/workstation-templates/${id}`).then((r) => r.data);

// Approval Chains
export const getApprovalChains = () => api.get("/config/approval-chains").then((r) => r.data);
export const createApprovalChain = (data) => api.post("/config/approval-chains", data).then((r) => r.data);

// Dashboard Stats
export const getDashboardStats = (userId, supervisedDeptIds) => {
    const params = {};
    if (userId) params.user_id = userId;
    if (supervisedDeptIds) params.supervised_dept_ids = supervisedDeptIds;
    return api.get("/stats/dashboard", { params }).then((r) => r.data);
};

// LDAP / Active Directory
export const getLdapConfig = () => api.get("/ldap/config").then((r) => r.data);
export const saveLdapConfig = (data) => api.post("/ldap/config", data).then((r) => r.data);
export const testLdapConnection = () => api.post("/ldap/test-connection").then((r) => r.data);
export const triggerLdapSync = () => api.post("/ldap/sync").then((r) => r.data);
export const previewLdapSync = () => api.get("/ldap/preview-sync").then((r) => r.data);

// Security Group Mappings
export const getSecurityGroupMappings = () => api.get("/ldap/mappings").then((r) => r.data);
export const createSecurityGroupMapping = (data) => api.post("/ldap/mappings", data).then((r) => r.data);
export const updateSecurityGroupMapping = (id, data) => api.put(`/ldap/mappings/${id}`, data).then((r) => r.data);
export const deleteSecurityGroupMapping = (id) => api.delete(`/ldap/mappings/${id}`).then((r) => r.data);

// User Group Exclusions
export const getUserGroupExclusions = (userId) =>
    api.get("/ldap/exclusions", { params: userId ? { user_id: userId } : {} }).then((r) => r.data);
export const createUserGroupExclusion = (data) => api.post("/ldap/exclusions", data).then((r) => r.data);
export const deleteUserGroupExclusion = (id) => api.delete(`/ldap/exclusions/${id}`).then((r) => r.data);

// User Manager (derived from department)
export const getUserManager = (userId) => api.get(`/users/${userId}/manager`).then((r) => r.data);
export const getUserEffectiveDepartment = (userId) => api.get(`/users/${userId}/effective-department`).then((r) => r.data);

// Integration Settings
export const getIntegrationSettings = () => api.get("/settings/integrations").then((r) => r.data);
export const saveEntraSettings = (data) => api.put("/settings/integrations/entra", data).then((r) => r.data);
export const saveLdapSettings = (data) => api.put("/settings/integrations/ldap", data).then((r) => r.data);
export const testEntraConnection = () => api.post("/settings/integrations/entra/test").then((r) => r.data);
export const testLdapConnectionV2 = () => api.post("/settings/integrations/ldap/test").then((r) => r.data);
export const triggerLdapSyncV2 = () => api.post("/settings/integrations/ldap/sync").then((r) => r.data);
export const getLdapSyncLogs = (limit = 10) => api.get("/settings/integrations/ldap/sync-logs", { params: { limit } }).then((r) => r.data);

// Ticket Categories (admin-configurable)
export const getTicketCategories = () => api.get("/config/ticket-categories").then((r) => r.data);
export const createTicketCategory = (data) => api.post("/config/ticket-categories", data).then((r) => r.data);
export const updateTicketCategory = (id, data) => api.put(`/config/ticket-categories/${id}`, data).then((r) => r.data);
export const deleteTicketCategory = (id) => api.delete(`/config/ticket-categories/${id}`).then((r) => r.data);

// Auth
export const authLogin = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);

// Attachments
export const uploadAttachment = (ticketId, file, authorId, commentId = null) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("author_id", authorId);
    if (commentId) formData.append("comment_id", commentId);
    return api.post(`/tickets/${ticketId}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
};
export const deleteAttachment = (id) => api.delete(`/attachments/${id}`).then((r) => r.data);
export const addTicketLinkAttachment = (ticketId, data) => api.post(`/tickets/${ticketId}/attachments/link`, data).then((r) => r.data);
export const getAttachmentUrl = (id) => `${API_BASE}/attachments/${id}/download`;
export const getAttachmentPreviewUrl = (id) => `${API_BASE}/attachments/${id}/preview`;

// Time Entries
export const getTimeEntries = (ticketId) => api.get(`/tickets/${ticketId}/time-entries`).then((r) => r.data);
export const createTimeEntry = (ticketId, agentId, data) => api.post(`/tickets/${ticketId}/time-entries?agent_id=${agentId}`, data).then((r) => r.data);
export const deleteTimeEntry = (id) => api.delete(`/time-entries/${id}`).then((r) => r.data);

// Ticket Links
export const getTicketLinks = (ticketId) => api.get(`/tickets/${ticketId}/links`).then((r) => r.data);
export const createTicketLink = (ticketId, data, actorId) => api.post(`/tickets/${ticketId}/links`, data, { params: { actor_id: actorId } }).then((r) => r.data);
export const deleteTicketLink = (ticketId, linkId, actorId) => api.delete(`/tickets/${ticketId}/links/${linkId}`, { params: { actor_id: actorId } }).then((r) => r.data);
export const closeChildren = (ticketId, data, actorId) => api.post(`/tickets/${ticketId}/close-children`, data, { params: { actor_id: actorId } }).then((r) => r.data);
export const searchTicketsQuick = (q, excludeId) => api.get("/tickets/search/quick", { params: { q, exclude_id: excludeId } }).then((r) => r.data);

// Knowledge Base
export const getKBArticles = (params = {}) => api.get("/kb/articles", { params }).then((r) => r.data);
export const getKBArticle = (id) => api.get(`/kb/articles/${id}`).then((r) => r.data);
export const createKBArticle = (data) => api.post("/kb/articles", data).then((r) => r.data);
export const updateKBArticle = (id, data) => api.put(`/kb/articles/${id}`, data).then((r) => r.data);
export const deleteKBArticle = (id) => api.delete(`/kb/articles/${id}`).then((r) => r.data);
export const voteKBArticle = (id, helpful) => api.post(`/kb/articles/${id}/helpful`, null, { params: { helpful } }).then((r) => r.data);
export const searchKBArticles = (q, type) => api.get("/kb/search", { params: { q, type } }).then((r) => r.data);

// Automation
export const getAutomationRules = () => api.get("/automation/rules").then((r) => r.data);
export const createAutomationRule = (data) => api.post("/automation/rules", data).then((r) => r.data);
export const updateAutomationRule = (id, data) => api.put(`/automation/rules/${id}`, data).then((r) => r.data);
export const deleteAutomationRule = (id) => api.delete(`/automation/rules/${id}`).then((r) => r.data);
export const getAutomationJobs = (params = {}) => api.get("/automation/jobs", { params }).then((r) => r.data);
export const getAutomationJob = (id) => api.get(`/automation/jobs/${id}`).then((r) => r.data);
export const retryAutomationJob = (id) => api.post(`/automation/jobs/${id}/retry`).then((r) => r.data);
export const cancelAutomationJob = (id) => api.post(`/automation/jobs/${id}/cancel`).then((r) => r.data);
export const triggerAutomation = (data) => api.post("/automation/trigger", data).then((r) => r.data);
export const getAutomationFailedCount = () => api.get("/automation/jobs/failed-count").then((r) => r.data);
export const getAutomationTemplates = () => api.get("/automation/templates").then((r) => r.data);
export const activateAutomationTemplate = (templateId, data = {}) => api.post(`/automation/templates/${templateId}/activate`, data).then((r) => r.data);
export const getSmtpStatus = () => api.get("/automation/smtp-status").then((r) => r.data);

// Checklists
export const getChecklists = (ticketId) => api.get(`/tickets/${ticketId}/checklists`).then((r) => r.data);
export const createChecklist = (ticketId, data) => api.post(`/tickets/${ticketId}/checklists`, data).then((r) => r.data);
export const createChecklistFromTemplate = (ticketId, templateId) => api.post(`/tickets/${ticketId}/checklists/from-template`, { template_id: templateId }).then((r) => r.data);
export const updateChecklist = (checklistId, data) => api.put(`/checklists/${checklistId}`, data).then((r) => r.data);
export const deleteChecklist = (checklistId) => api.delete(`/checklists/${checklistId}`).then((r) => r.data);
export const toggleChecklistItem = (checklistId, itemId) => api.put(`/checklists/${checklistId}/items/${itemId}/toggle`).then((r) => r.data);
export const addChecklistItem = (checklistId, label) => api.post(`/checklists/${checklistId}/items`, { label }).then((r) => r.data);
export const deleteChecklistItem = (checklistId, itemId) => api.delete(`/checklists/${checklistId}/items/${itemId}`).then((r) => r.data);

// Checklist Templates
export const getChecklistTemplates = (ticketType) => api.get(`/checklist-templates${ticketType ? `?ticket_type=${ticketType}` : ""}`).then((r) => r.data);
export const getAllChecklistTemplates = () => api.get("/checklist-templates/all").then((r) => r.data);
export const createChecklistTemplate = (data) => api.post("/checklist-templates", data).then((r) => r.data);
export const updateChecklistTemplate = (id, data) => api.put(`/checklist-templates/${id}`, data).then((r) => r.data);
export const deleteChecklistTemplate = (id) => api.delete(`/checklist-templates/${id}`).then((r) => r.data);

// System settings
export const getSystemSettings = () => api.get("/system/settings").then((r) => r.data);
export const updateSystemSettings = (data) => api.put("/system/settings", data).then((r) => r.data);
export const triggerAutoClose = () => api.post("/system/auto-close").then((r) => r.data);
export const triggerReminders = () => api.post("/system/send-reminders").then((r) => r.data);

// Macros
export const getMacros = () => api.get("/macros").then((r) => r.data);
export const getAllMacros = () => api.get("/macros/all").then((r) => r.data);
export const createMacro = (data) => api.post("/macros", data).then((r) => r.data);
export const updateMacro = (id, data) => api.put(`/macros/${id}`, data).then((r) => r.data);
export const deleteMacro = (id) => api.delete(`/macros/${id}`).then((r) => r.data);
export const applyMacro = (ticketId, macroId, actorId) =>
    api.post(`/tickets/${ticketId}/apply-macro`, { macro_id: macroId, actor_id: actorId }).then((r) => r.data);

// Backups (9B)
export const triggerBackup = () => api.post("/admin/backups/trigger").then((r) => r.data);
export const listBackups = () => api.get("/admin/backups/list").then((r) => r.data);
export const downloadBackupUrl = (name) => `${API_BASE}/admin/backups/download/${encodeURIComponent(name)}`;
export const deleteBackup = (name) => api.delete(`/admin/backups/${encodeURIComponent(name)}`).then((r) => r.data);

// Admin Soft Delete (9A)
export const getDeletedItems = (collection) => api.get(`/admin/deleted/${collection}`).then((r) => r.data);
export const restoreDeletedItem = (collection, id) => api.post(`/admin/restore/${collection}/${id}`).then((r) => r.data);
export const purgeDeletedItem = (collection, id) => api.delete(`/admin/purge/${collection}/${id}`).then((r) => r.data);
export const getDeletedCounts = () => api.get("/admin/deleted-counts").then((r) => r.data);
export const softDeleteTicket = (id) => api.delete(`/admin/tickets/${id}`).then((r) => r.data);

// Archive / Delete tickets
export const archiveTicket = (id, actorId) =>
    api.post(`/tickets/${id}/archive`, null, { params: { actor_id: actorId } }).then((r) => r.data);
export const unarchiveTicket = (id, actorId) =>
    api.post(`/tickets/${id}/unarchive`, null, { params: { actor_id: actorId } }).then((r) => r.data);
export const deleteTicket = (id) =>
    api.delete(`/tickets/${id}`).then((r) => r.data);

// Seed Database
export const seedDatabase = () => api.post("/seed").then((r) => r.data);

// SSO
export const getSsoStatus = () => api.get("/auth/sso/status").then((r) => r.data);

// Entra mappings & sync
export const getEntraMappings = () => api.get("/entra/mappings").then((r) => r.data);
export const createEntraMapping = (data) => api.post("/entra/mappings", data).then((r) => r.data);
export const updateEntraMapping = (id, data) => api.put(`/entra/mappings/${id}`, data).then((r) => r.data);
export const deleteEntraMapping = (id) => api.delete(`/entra/mappings/${id}`).then((r) => r.data);
export const triggerEntraSync = () => api.post("/entra/sync").then((r) => r.data);
export const getEntraSyncStatus = () => api.get("/entra/sync/status").then((r) => r.data);
export const getEntraGroups = () => api.get("/entra/groups").then((r) => r.data);

// Admin reset
export const resetData = () => api.post("/admin/reset-data?confirm=RESET").then((r) => r.data);

export default api;

// AI
export const getAiStatus = () => api.get("/ai/status").then((r) => r.data);
export const reformulateDescription = (data) => api.post("/ai/reformulate", data).then((r) => r.data);
export const getAiConfig = () => api.get("/ai/config").then((r) => r.data);
export const saveAiConfig = (data) => api.put("/ai/config", data).then((r) => r.data);

// AI — Comment/Contact/Solution reformulation
export const reformulateComment = (data) => api.post("/ai/reformulate-comment", data).then((r) => r.data);

// Solutions
export const getTicketSolutions = (ticketId) => api.get(`/tickets/${ticketId}/solutions`).then((r) => r.data);
export const createSolution = (ticketId, data, actorId) => api.post(`/tickets/${ticketId}/solutions`, data, { params: { actor_id: actorId } }).then((r) => r.data);
export const updateSolution = (solutionId, data, actorId) => api.put(`/solutions/${solutionId}`, data, { params: { actor_id: actorId } }).then((r) => r.data);
export const deleteSolution = (solutionId, actorId) => api.delete(`/solutions/${solutionId}`, { params: { actor_id: actorId } }).then((r) => r.data);
export const toggleRetainSolution = (solutionId, actorId) => api.post(`/solutions/${solutionId}/retain`, null, { params: { actor_id: actorId } }).then((r) => r.data);

// Team requests
export const createTeamRequest = (data) => api.post("/team-requests", data).then((r) => r.data);
export const getTeamRequests = (params) => api.get("/team-requests", { params }).then((r) => r.data);
export const getTeamRequestsPendingCount = () => api.get("/team-requests/pending-count").then((r) => r.data);
export const approveTeamRequest = (id) => api.post(`/team-requests/${id}/approve`).then((r) => r.data);
export const rejectTeamRequest = (id, data) => api.post(`/team-requests/${id}/reject`, data).then((r) => r.data);
export const cancelTeamRequest = (id) => api.post(`/team-requests/${id}/cancel`).then((r) => r.data);

